import { Component, Input, inject } from '@angular/core';
import { Assessment } from '$synapse/models';
import { SynDialogConfig } from '../../utils/syn-dialog-config';
import { ConfirmModalComponent } from '../confirm-modal/confirm-modal.component';
import { AssessmentService, StatsService } from '$synapse/services';
import { Router } from '@angular/router';
import { LessonImportComponent } from '../../../modules/assessments/lesson-import/lesson-import.component';
import { MatDialog } from '@angular/material/dialog';
import { SHARED_IMPORTS } from '../../shared-imports';
import { CertifiedAssessmentIconComponent } from '../certified-assessment-icon/certified-assessment-icon.component';

@Component({
  selector: 'syn-assessment-card',
  templateUrl: './assessment-card.component.html',
  styleUrls: ['./assessment-card.component.scss'],
  imports: [SHARED_IMPORTS, CertifiedAssessmentIconComponent],
})
export class AssessmentCardComponent {
  private readonly _assessmentService = inject(AssessmentService);
  private readonly _statsService = inject(StatsService);
  private readonly _router = inject(Router);
  private readonly _modalDialog = inject(MatDialog);

  @Input() assessment!: Assessment;

  configureMatDialog(type: string): ConfirmModalComponent {
    const modalConf = new SynDialogConfig(type);
    const dialogRef = this._modalDialog.open(ConfirmModalComponent, modalConf);
    return dialogRef.componentInstance;
  }

  deleteAssessment(assessmentId: string) {
    const instance = this.configureMatDialog('delete-assessment-confirmation');
    instance.title = "Suppression d'examen";
    instance.message =
      'Êtes-vous sûr de vouloir supprimer cet examen ? Toutes les sessions ' +
      'associées seront supprimées.';
    instance.action = 'Supprimer';
    instance.confirmationEvent.subscribe(() => {
      this._assessmentService.delete(assessmentId).subscribe(() => {
        this._router.navigate(['/questionnaires']);
      });
    });
  }

  makeCertifying(assessmentId: string) {
    const instance = this.configureMatDialog('make-assessment-certifying');
    instance.title = "Certification d'un questionnaire";
    instance.message =
      'Êtes-vous sûr de vouloir rendre ce questionnaire certifiant ? Toutes les ' +
      'informations liées à ce questionnaire ne seront plus modifiables';
    instance.action = 'Certifier';
    instance.confirmationEvent.subscribe(() => {
      this._assessmentService.certifyAssessment(assessmentId).subscribe(() => {
        this._router.navigate(['/questionnaires']);
      });
    });
  }

  async downloadStatsReport(
    assessmentId: string,
    assessmentName: string
  ): Promise<void> {
    this._statsService
      .downloadAssessmentStatsReport(assessmentId)
      .subscribe((blob: Blob): void => {
        this._createDownloadLink(blob, assessmentName);
      });
  }

  async downloadAssessmentCsv(
    assessmentId: string,
    assessmentName: string
  ): Promise<void> {
    this._assessmentService
      .downloadAssessmentCsv(assessmentId)
      .subscribe((blob: Blob): void => {
        if (blob !== null) {
          this._createDownloadLink(blob, assessmentName);
        }
      });
  }

  downloadLesson(assessmentId: string) {
    this._assessmentService.downloadLesson(assessmentId).subscribe((pdf) => {
      window.open(pdf.presignedUrl, '_blank');
    });
  }

  uploadNewLesson(assessmentId: string) {
    const dialogRef = this._modalDialog.open(LessonImportComponent, {
      data: { assessmentId: assessmentId },
    });
    dialogRef.afterClosed().subscribe(() => {
      this._router.navigateByUrl('/questionnaires');
    });
  }

  previewAssessment(assessmentId: string) {
    this._router.navigate(['/questionnaires', assessmentId, 'preview']);
  }

  private _createDownloadLink(blob: Blob, fileName: string) {
    const link: HTMLAnchorElement = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    const todayDate: string = new Date().toISOString().slice(0, 10);
    link.download = `${fileName}_${todayDate}.csv`;
    link.click();
  }
}
